// src/app/features/owner/orders/orders.service.ts

import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ApiService } from '../../../core/services/api.service';
import { WebsocketService } from '../../../core/services/websocket.service';
import { Order, OrderStatus, BillResponse } from './order.model';
import { Payment, CollectCashRequest, DailyCashSummaryResponse, PaymentResponse } from './payment.model';

@Injectable({
  providedIn: 'root'
})
export class OrdersService {
  private api = inject(ApiService);
  private ws = inject(WebsocketService);
  private readonly baseUrl = '/owner/orders';
  private readonly paymentUrl = '/owner/payments';

  // Real-time kitchen events
  kitchenEvents$ = this.ws.kitchenEvents$;

  getOrders(status?: string, date?: string, startDate?: string, endDate?: string): Observable<Order[]> {
    const params: any = {};
    if (status) params.status = status;
    if (date) params.date = date;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    return this.api.get<Order[]>(this.baseUrl, params).pipe(map(res => res.data));
  }

  getActiveOrders(): Observable<Order[]> {
    return this.api.get<Order[]>(`${this.baseUrl}/active`).pipe(map(res => res.data));
  }

  getOrderById(id: string): Observable<Order> {
    return this.api.get<Order>(`${this.baseUrl}/${id}`).pipe(map(res => res.data));
  }

  updateStatus(id: string, status: OrderStatus): Observable<Order> {
    return this.api.patch<Order>(`${this.baseUrl}/${id}/status`, { status }).pipe(map(res => res.data));
  }

  updateOrder(id: string, payload: any): Observable<Order> {
    return this.api.put<Order>(`${this.baseUrl}/${id}`, payload).pipe(map(res => res.data));
  }

  cancelOrder(id: string, reason: string): Observable<Order> {
    return this.api.post<Order>(`${this.baseUrl}/${id}/cancel?reason=${encodeURIComponent(reason)}`, {}).pipe(map(res => res.data));
  }

  generateBill(id: string): Observable<BillResponse> {
    return this.api.get<BillResponse>(`${this.baseUrl}/${id}/bill`).pipe(map(res => res.data));
  }

  collectCash(orderId: string, req: CollectCashRequest): Observable<PaymentResponse> {
    return this.api.post<PaymentResponse>(`${this.paymentUrl}/orders/${orderId}/collect`, req).pipe(map(res => res.data));
  }

  getDailySummary(date: string): Observable<DailyCashSummaryResponse> {
    return this.api.get<DailyCashSummaryResponse>(`${this.paymentUrl}/summary/daily`, { date }).pipe(map(res => res.data));
  }
}
